import { useMemo, useState, type ReactNode } from "react";
import type { ItemProprio } from "@bookdental/schemas";
import { CatalogoBusca, combina } from "@/components/catalogo/CatalogoBusca";
import { AdicionarItemProprio } from "./AdicionarItemProprio";
import { LayoutDeSelecao } from "./selecao/LayoutDeSelecao";
import { GradeDeCategorias, type FocoNaCategoria } from "./selecao/GradeDeCategorias";
import { SeletorDeModo, type ModoDaGrade } from "./selecao/SeletorDeModo";
import type { ItemEscolhido } from "./selecao/ResumoDaSelecao";

export type ServicoDaBase = { id: string; nome: string; categoria: string; perfis: string[]; materiais: string[] };

/**
 * Passo 1: os serviços da base. Por categoria, cartões fechados — abrir um mostra os serviços logo
 * abaixo; em Lista, tudo aberto. O resumo à direita leva ao próximo passo. Os perfis do
 * laboratório não aparecem: saem dos serviços escolhidos.
 */
export function PassoServicos({
  servicos,
  marcados,
  onChange,
  proprios,
  onProprios,
  acao,
}: {
  servicos: ServicoDaBase[];
  marcados: string[];
  onChange: (ids: string[]) => void;
  proprios: ItemProprio[];
  onProprios: (itens: ItemProprio[]) => void;
  acao: ReactNode;
}) {
  const [busca, setBusca] = useState("");
  const [foco, setFoco] = useState<FocoNaCategoria>(null);
  const [modo, setModo] = useState<ModoDaGrade>("categorias");
  const selecao = useMemo(() => new Set(marcados), [marcados]);

  const alterar = (ids: string[], marcar: boolean) => {
    // Desmarcar um próprio (clique nele) é tirá-lo; o "Desmarcar todos" da categoria não apaga o que a pessoa criou.
    const propriosFora = new Set(ids.length === 1 && !marcar ? ids.filter((id) => id.startsWith("proprio:")) : []);
    if (propriosFora.size) onProprios(proprios.filter((_, i) => !propriosFora.has(`proprio:${i}`)));
    const novo = new Set(selecao);
    for (const id of ids.filter((i) => !i.startsWith("proprio:"))) {
      if (marcar) novo.add(id);
      else novo.delete(id);
    }
    onChange([...novo]);
  };

  const daBase = (busca.trim() ? servicos.filter((s) => combina(s.nome, busca)) : servicos).map((s) => ({
    id: s.id,
    nome: s.nome,
    categoria: s.categoria,
    marcado: selecao.has(s.id),
  }));
  // Os próprios aparecem na própria categoria, já marcados.
  const visiveis = [
    ...daBase,
    ...proprios
      .map((p, i) => ({ id: `proprio:${i}`, nome: p.nome, categoria: p.categoria, marcado: true }))
      .filter((p) => !busca.trim() || combina(p.nome, busca)),
  ];

  const escolhidos: ItemEscolhido[] = [
    ...servicos.filter((s) => selecao.has(s.id)).map((s) => ({ id: s.id, nome: s.nome, categoria: s.categoria })),
    ...proprios.map((p, i) => ({ id: `proprio:${i}`, nome: p.nome, categoria: p.categoria, proprio: true })),
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-foreground">Quais serviços você faz?</h2>
        <p className="text-sm text-zinc-500 dark:text-muted-foreground">
          Abra uma categoria e toque nos serviços que você faz. Os materiais de cada um você confere no próximo passo; o que
          ficar de fora dá pra adicionar depois no Catálogo.
        </p>
      </div>

      <LayoutDeSelecao
        acao={acao}
        resumo={{
          itens: escolhidos,
          unidade: "serviços",
          onRemover: (itens) => {
            alterar(itens.filter((i) => !i.proprio).map((i) => i.id), false);
            const tirar = new Set(itens.filter((i) => i.proprio).map((i) => i.id));
            if (tirar.size) onProprios(proprios.filter((_, i) => !tirar.has(`proprio:${i}`)));
          },
          onIrPara: (categoria) => {
            setBusca("");
            setFoco({ categoria, vez: Date.now() });
          },
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <CatalogoBusca valor={busca} onChange={setBusca} placeholder="Buscar serviço" achados={visiveis.length} className="mt-0 min-w-[200px] flex-1" />
          <SeletorDeModo modo={modo} onChange={setModo} rotuloDasCategorias="Por categoria" />
        </div>
        {visiveis.length === 0 ? (
          <p className="rounded-xl border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-500 dark:border-border dark:text-muted-foreground">
            Nenhum serviço encontrado.
          </p>
        ) : (
          <GradeDeCategorias itens={visiveis} onAlterar={alterar} unidade="serviços" fechadas={modo === "categorias" && !busca.trim()} recolhivel={modo === "lista" && !busca.trim()} foco={foco} onAdicionarNaCategoria={(categoria, nome) => onProprios([...proprios, { nome, categoria }])} />
        )}
        <AdicionarItemProprio
          rotulo="Adicionar serviço que não está na lista"
          categorias={[...new Set(servicos.map((s) => s.categoria))]}
          onAdicionar={(item) => onProprios([...proprios, item])}
        />
      </LayoutDeSelecao>
    </div>
  );
}
